/**
 * pixi-cjk v8 适配版
 *
 * 基于 pixi-cjk (https://github.com/huang-yuwei/pixi-cjk) 的 CJK 禁则规则，
 * 通过覆写 PixiJS v8 的 CanvasTextMetrics 静态钩子方法实现中日韩文字的正确换行。
 *
 * PixiJS v8 提供了以下可覆写的静态方法（见 CanvasTextMetrics）：
 * - canBreakChars(char, nextChar, token, index, breakWords): 判断两个字符之间是否可以断行
 * - isBreakingSpace(char, nextChar): 判断字符是否为可断行的空白
 *
 * 用法：
 *   import './utils/pixiCJK';  // 副作用导入，自动安装
 *   // 或
 *   import { installCJKSupport, uninstallCJKSupport } from './utils/pixiCJK';
 *   installCJKSupport();  // 手动安装
 */

import { CanvasTextMetrics, BREAK_AFTER_CHARS, BREAK_AFTER_CHARS_SET } from 'pixi.js';

// ============================================================
// 禁则规则（Kinsoku Shori）正则 — 来源于原 pixi-cjk 库
// 参考：https://en.wikipedia.org/wiki/Line_breaking_rules_in_East_Asian_languages
// ============================================================

/** 简体中文：不能出现在行首的字符 */
const regexCannotStartZhCn =
  /[!%),.:;?\]}¢°·'""”’†‡›℃∶、。〃〆〕〗〞﹚﹜！＂％＇），．：；？！］｝～]/;

/** 简体中文：不能出现在行尾的字符 */
const regexCannotEndZhCn = /[$(£¥·“‘'"〈《「『【〔〖〝﹙﹛＄（．［｛￡￥]/;

/** 繁体中文：不能出现在行首的字符 */
const regexCannotStartZhTw =
  /[!),.:;?\]}¢·–'"”’•"、。〆〞〕〉》」︰︱︲︳﹐﹑﹒﹓﹔﹕﹖﹘﹚﹜！），．：；？︶︸︺︼︾﹀﹂﹗］｜｝､]/;

/** 繁体中文：不能出现在行尾的字符 */
const regexCannotEndZhTw = /[([{£¥“‘'"‵〈《「『〔〝︴﹙﹛（｛︵︷︹︻︽︿﹁﹃﹏]/;

/** 日文：不能出现在行首的字符 */
const regexCannotStartJaJp =
  /[)\]｝〕〉》」』】〙〗〟”’'"｠»ヽヾーァィゥェォッャュョヮヵヶぁぃぅぇぉっゃゅょゎゕゖㇰㇱㇲㇳㇴㇵㇶㇷㇸㇹㇺㇻㇼㇽㇾㇿ々〻‐゠–〜?!‼⁇⁈⁉・、:;,。.]/;

/** 日文：不能出现在行尾的字符 */
const regexCannotEndJaJp = /[([｛〔〈《「『【〘〖〝“‘'"｟«...‥〳〴〵]/;

/** 韩文：不能出现在行首的字符 */
const regexCannotStartKoKr = /[!%),.:;?\]}¢°”’'"†‡℃〆〈《「『〕！％），．：；？］｝]/;

/** 韩文：不能出现在行尾的字符 */
const regexCannotEndKoKr = /[$([{£¥“‘'"々〇〉》」〔＄（［｛｠￥￦#]/;

/** 合并后的"不能出现在行首"正则 */
const regexCannotStart = new RegExp(
  `${regexCannotStartZhCn.source}|${regexCannotStartZhTw.source}|${regexCannotStartJaJp.source}|${regexCannotStartKoKr.source}`
);

/** 合并后的"不能出现在行尾"正则 */
const regexCannotEnd = new RegExp(
  `${regexCannotEndZhCn.source}|${regexCannotEndZhTw.source}|${regexCannotEndJaJp.source}|${regexCannotEndKoKr.source}`
);

/** 拉丁字符正则（来源于原 pixi-cjk），非拉丁字符不应在空格处断行 */
const LATIN_REGEX = /[a-zA-Z0-9À-ÖØ-öø-ÿ!"#$%&'()*+,-./:;]/;

// ============================================================
// 保存原始方法/状态引用
// ============================================================
let installed = false;
let originalCanBreakChars: typeof CanvasTextMetrics.canBreakChars | null = null;
let originalIsBreakingSpace: typeof CanvasTextMetrics.isBreakingSpace | null = null;
/** 被移除的 break-after 字符（用于卸载时恢复） */
let removedBreakAfterChars: number[] = [];

/**
 * 安装 CJK 换行支持。
 *
 * 覆写 CanvasTextMetrics 的两个静态钩子：
 * 1. canBreakChars — 加入禁则规则，防止标点出现在行首/行尾
 * 2. isBreakingSpace — 防止 CJK 字符前的空格被当作断行点
 *
 * 同时从 BREAK_AFTER_CHARS 中移除 em dash（—）和 en dash（–），
 * 防止 tokenize 在 `——` 之间切分 token，导致 CJK 禁则规则无法生效。
 *
 * 可安全地多次调用（幂等）。
 */
export function installCJKSupport(): void {
  if (installed) return;

  // 保存原始实现
  if (!originalCanBreakChars) {
    originalCanBreakChars = CanvasTextMetrics.canBreakChars;
  }
  if (!originalIsBreakingSpace) {
    originalIsBreakingSpace = CanvasTextMetrics.isBreakingSpace;
  }

  // 覆写 canBreakChars：当 char 不能出现在行尾，或 nextChar 不能出现在行首时，禁止断行
  CanvasTextMetrics.canBreakChars = function (
    char: string,
    nextChar: string,
    token: string,
    index: number,
    breakWords: boolean
  ): boolean {
    // 禁则检查：char 不能结束一行
    if (regexCannotEnd.test(char)) {
      return false;
    }
    // 禁则检查：nextChar 不能开始一行
    if (regexCannotStart.test(nextChar)) {
      return false;
    }
    // 破折号之间不允许断行（——、––、–—、—–）
    if ((char === '—' || char === '–') && (nextChar === '—' || nextChar === '–')) {
      return false;
    }
    // 回退到默认逻辑
    return originalCanBreakChars!.call(this, char, nextChar, token, index, breakWords);
  };

  // 覆写 isBreakingSpace：当空格后面是非拉丁字符时，不应作为断行空格（与原 pixi-cjk 一致）
  CanvasTextMetrics.isBreakingSpace = function (char: string, nextChar?: string): boolean {
    if (nextChar && !LATIN_REGEX.test(nextChar)) {
      return false;
    }
    return originalIsBreakingSpace!.call(this, char, nextChar);
  };

  // 从 BREAK_AFTER_CHARS 中移除 em dash（U+2014, code 8212）和 en dash（U+2013, code 8211）
  // 否则 PixiJS 的 tokenize 会在 `——` 之间切分 token，导致两个 em dash 被拆散，
  // wordWrap 在 token 之间断行时不经过 canBreakChars，CJK 禁则规则拦不住
  const DASH_CHARS = [8211, 8212]; // en dash (–), em dash (—)
  for (const code of DASH_CHARS) {
    const idx = BREAK_AFTER_CHARS.indexOf(code);
    if (idx !== -1) {
      BREAK_AFTER_CHARS.splice(idx, 1);
      BREAK_AFTER_CHARS_SET.delete(code);
      removedBreakAfterChars.push(code);
    }
  }

  installed = true;
}

/**
 * 卸载 CJK 换行支持，恢复 PixiJS 默认行为。
 */
export function uninstallCJKSupport(): void {
  if (!installed) return;

  if (originalCanBreakChars) {
    CanvasTextMetrics.canBreakChars = originalCanBreakChars;
  }
  if (originalIsBreakingSpace) {
    CanvasTextMetrics.isBreakingSpace = originalIsBreakingSpace;
  }

  // 恢复被移除的 break-after 字符
  for (const code of removedBreakAfterChars) {
    BREAK_AFTER_CHARS.push(code);
    BREAK_AFTER_CHARS_SET.add(code);
  }
  removedBreakAfterChars = [];

  installed = false;
}

// ============================================================
// 副作用导入时自动安装（与原 pixi-cjk 行为一致）
// ============================================================
installCJKSupport();
